import * as React from "react";
import * as Sentry from "@sentry/react";
import { useDropzone } from "react-dropzone";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  FILES_TAGS,
  type FamilyGroupingMode,
  checkFileExists,
  filesApi,
  useCreateUploadSessionsMutation,
  usePrepareDirectoryUploadMutation,
  useSealDirectoryUploadOperationMutation,
  useTriggerUploadIngestionMutation,
} from "@/api/files";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody } from "@/components/ui/table/table";
import * as analytics from "@/services/analytics";
import { useAppDispatch } from "@/store/hooks";
import { useContractFamiliesAccess } from "@/hooks/use-contract-families-access";
import { UploadExistsFilesConfirmationModal } from "../uploadExistsFilesConfirmationModal";
import { runUploadOperation, type PreparedUploadFile, type UploadDestination } from "../../upload/uploadCoordinator";
import {
  collectSelectedFiles,
  DirectoryTraversalUnsupportedError,
  InvalidDroppedPathError,
} from "../../upload/directoryCollector";
import {
  collectDropzoneEventFiles,
  type DropzoneFilesEvent,
  type UploadCollectionOutcome,
} from "../../upload/dropzoneEventCollector";
import { getUploadFilename, type CollectedUploadFile } from "../../upload/types";
import { ACCEPTED_EXTENSIONS, MAX_UPLOAD_SIZE } from "./consts";
import { ContractFamilyProcessing, type ContractFamilyProcessingForm } from "./contractFamilyProcessing";
import { UploadModalErrors } from "./errors";
import { UploadModalFilesHeader } from "./filesHeader";
import { UploadModalFilesRow } from "./filesRow";
import { formatFileErrors, normalizeDisplayProgress, validateUploadFile } from "./helpers";
import { UploadModalPermissionNotification } from "./permissionNotifications";
import { UploadSelection } from "./uploadSelection";
import { uploadFileResumable } from "./resumableUpload";
import { type FileWithProgress, type UpdateUploadRows, type UploadConflictFile } from "./types";
import { UploadModalWebsocket } from "./websocket";

interface Props {
  destination: UploadDestination;
  initialFiles?: readonly CollectedUploadFile[];
  collectionMessage?: string;
  isVisible?: boolean;
  onClose: () => void;
  onBusyChange?: (busy: boolean) => void;
  onOperationStarted?: (operationId: string) => void;
}

interface ConflictRequest {
  files: readonly UploadConflictFile[];
  resolve: (relativePaths: ReadonlySet<string>) => void;
}

const BUSY_DESCRIPTION_ID = "upload-dialog-busy-description";

export const UploadModalContent: React.FC<Props> = ({
  destination,
  initialFiles,
  collectionMessage,
  isVisible = true,
  onClose,
  onBusyChange,
  onOperationStarted,
}) => {
  const form = useForm<ContractFamilyProcessingForm>({
    defaultValues: { organiseAsContractFamilies: false, familyGroupingMode: "automatic" },
  });
  const { hasContractFamilies } = useContractFamiliesAccess();
  const dispatch = useAppDispatch();
  const [prepareDirectoryUpload] = usePrepareDirectoryUploadMutation();
  const [sealDirectoryUploadOperation] = useSealDirectoryUploadOperationMutation();
  const [createUploadSessions] = useCreateUploadSessionsMutation();
  const [triggerUploadIngestion] = useTriggerUploadIngestionMutation();
  const [files, setFiles] = React.useState<FileWithProgress[]>([]);
  const [selectionMessage, setSelectionMessage] = React.useState(collectionMessage);
  const [selectedSourceFiles, setSelectedSourceFiles] = React.useState<readonly CollectedUploadFile[]>(
    initialFiles ?? [],
  );
  const rowsRef = React.useRef<FileWithProgress[]>([]);
  const initializedRef = React.useRef(false);
  const pendingUploadRef = React.useRef<readonly CollectedUploadFile[] | null>(null);
  const pendingCollectionRef = React.useRef<UploadCollectionOutcome | null>(null);
  const permissionGrantedRef = React.useRef(false);
  const directoryStartRequestedRef = React.useRef(false);
  const activeTxIdsRef = React.useRef<ReadonlySet<string>>(new Set());
  const mountedRef = React.useRef(true);
  const operationGenerationRef = React.useRef(0);
  const operationInFlightRef = React.useRef(false);
  const operationInFlightGenerationRef = React.useRef<number | null>(null);
  const operationCoordinatorSettledRef = React.useRef(true);
  const conflictRequestRef = React.useRef<ConflictRequest | null>(null);
  const onBusyChangeRef = React.useRef(onBusyChange);
  const onCloseRef = React.useRef(onClose);
  const onOperationStartedRef = React.useRef(onOperationStarted);
  const [isOperationRunning, setIsOperationRunning] = React.useState(false);
  const [uploadStarted, setUploadStarted] = React.useState(false);
  const [permissionGranted, setPermissionGranted] = React.useState(false);
  const [isPreparingFolders, setIsPreparingFolders] = React.useState(false);
  const [errorsNotified, setErrorsNotified] = React.useState(false);
  const [conflictRequest, setConflictRequest] = React.useState<ConflictRequest | null>(null);

  React.useLayoutEffect(() => {
    onBusyChangeRef.current = onBusyChange;
    onCloseRef.current = onClose;
    onOperationStartedRef.current = onOperationStarted;
  }, [onBusyChange, onClose, onOperationStarted]);

  const releaseOperationIfSettled = React.useCallback((operationGeneration?: number) => {
    const inFlightGeneration = operationInFlightGenerationRef.current;
    if (
      inFlightGeneration === null ||
      (operationGeneration !== undefined && operationGeneration !== inFlightGeneration) ||
      operationGenerationRef.current !== inFlightGeneration ||
      !operationCoordinatorSettledRef.current ||
      rowsRef.current.some(row => row.status !== "completed" && row.status !== "error")
    ) {
      return;
    }
    operationInFlightRef.current = false;
    operationInFlightGenerationRef.current = null;
  }, []);

  const updateRows = React.useCallback<UpdateUploadRows>(
    update => {
      if (!mountedRef.current) return;
      const nextRows = update(rowsRef.current);
      rowsRef.current = nextRows;
      setFiles(nextRows);
      releaseOperationIfSettled();
    },
    [releaseOperationIfSettled],
  );

  const confirmConflictFiles = React.useCallback(
    (conflicts: readonly UploadConflictFile[]): Promise<ReadonlySet<string>> =>
      new Promise(resolve => {
        if (!mountedRef.current) {
          resolve(new Set());
          return;
        }
        conflictRequestRef.current?.resolve(new Set());
        const request = { files: conflicts, resolve };
        conflictRequestRef.current = request;
        setConflictRequest(request);
      }),
    [],
  );

  const resolveConflicts = React.useCallback((relativePaths: ReadonlySet<string>) => {
    conflictRequestRef.current?.resolve(relativePaths);
    conflictRequestRef.current = null;
    if (mountedRef.current) setConflictRequest(null);
  }, []);

  const confirmFlatConflicts = React.useCallback(
    async (collectedFiles: readonly CollectedUploadFile[]): Promise<readonly CollectedUploadFile[]> => {
      const existence = await Promise.all(
        collectedFiles.map(async file => ({ file, exists: await checkFileExists(getUploadFilename(file)) })),
      );
      const conflicts = existence
        .filter(result => result.exists)
        .map(({ file }) => ({
          clientId: crypto.randomUUID(),
          relativePath: file.relativePath,
        }));
      if (conflicts.length === 0) return collectedFiles;
      const confirmedPaths = await confirmConflictFiles(conflicts);
      return existence
        .filter(result => !result.exists || confirmedPaths.has(result.file.relativePath))
        .map(result => result.file);
    },
    [confirmConflictFiles],
  );

  const startUpload = React.useCallback(
    async (collectedFiles: readonly CollectedUploadFile[], familyGroupingMode: FamilyGroupingMode = "none") => {
      if (collectedFiles.length === 0 || operationInFlightRef.current) return;
      operationInFlightRef.current = true;
      setUploadStarted(true);
      const operationGeneration = operationGenerationRef.current + 1;
      operationGenerationRef.current = operationGeneration;
      operationInFlightGenerationRef.current = operationGeneration;
      operationCoordinatorSettledRef.current = false;
      const isCurrentOperation = () => mountedRef.current && operationGenerationRef.current === operationGeneration;
      const requiresPreparation = collectedFiles.some(file => file.directoryPath.length > 0);
      onBusyChangeRef.current?.(true);
      setIsOperationRunning(true);
      setIsPreparingFolders(requiresPreparation);
      setErrorsNotified(false);

      try {
        const uploadFiles = requiresPreparation ? collectedFiles : await confirmFlatConflicts(collectedFiles);
        if (uploadFiles.length === 0) return;
        const result = await runUploadOperation(
          { files: uploadFiles, destination, familyGroupingMode },
          {
            prepare: async request => {
              try {
                return await prepareDirectoryUpload(request).unwrap();
              } finally {
                if (isCurrentOperation()) setIsPreparingFolders(false);
              }
            },
            seal: request => sealDirectoryUploadOperation(request).unwrap(),
            createSessions: request => createUploadSessions(request).unwrap(),
            uploadFile: async (file, putPath, onProgress) => {
              try {
                await uploadFileResumable(file, putPath, { onProgress });
                analytics.documentUpload(file.name, file.size);
              } catch (error) {
                Sentry.captureException(error);
                throw error;
              }
            },
            triggerIngestion: request => triggerUploadIngestion(request).unwrap(),
            confirmConflicts: (conflicts: readonly PreparedUploadFile[]) =>
              confirmConflictFiles(
                conflicts.map(file => ({ clientId: file.clientId, relativePath: file.relativePath })),
              ),
            getRows: () => rowsRef.current,
            registerTransaction: registration => {
              if (!isCurrentOperation() || activeTxIdsRef.current.has(registration.txId)) return;
              activeTxIdsRef.current = new Set([...activeTxIdsRef.current, registration.txId]);
            },
            updateRows: update => {
              if (isCurrentOperation()) updateRows(update);
            },
          },
        );
        if (isCurrentOperation() && result.operationId && result.sealAcknowledged === false) {
          toast.error("Upload finalization could not be confirmed. The operation remains available for recovery.");
        }
        if (isCurrentOperation() && result.operationId) onOperationStartedRef.current?.(result.operationId);
      } finally {
        if (isCurrentOperation()) {
          operationCoordinatorSettledRef.current = true;
          setIsPreparingFolders(false);
          setIsOperationRunning(false);
          dispatch(filesApi.util.invalidateTags([FILES_TAGS.FILES_SEARCH_TAG, FILES_TAGS.FILES_TAG]));
          releaseOperationIfSettled(operationGeneration);
        }
      }
    },
    [
      confirmConflictFiles,
      confirmFlatConflicts,
      createUploadSessions,
      destination,
      dispatch,
      prepareDirectoryUpload,
      releaseOperationIfSettled,
      sealDirectoryUploadOperation,
      triggerUploadIngestion,
      updateRows,
    ],
  );

  const startUploadRef = React.useRef(startUpload);
  React.useLayoutEffect(() => {
    startUploadRef.current = startUpload;
  }, [startUpload]);

  const queueUploadUntilPermissionResolved = React.useCallback((collectedFiles: readonly CollectedUploadFile[]) => {
    if (collectedFiles.length === 0) return;
    const hasDirectoryPaths = collectedFiles.some(file => file.directoryPath.length > 0);
    if (hasDirectoryPaths) {
      pendingUploadRef.current = collectedFiles;
      return;
    }
    if (permissionGrantedRef.current) {
      void startUploadRef.current(collectedFiles);
      return;
    }
    pendingUploadRef.current = collectedFiles;
  }, []);

  const handlePermissionProceed = React.useCallback(() => {
    if (permissionGrantedRef.current) return;
    permissionGrantedRef.current = true;
    setPermissionGranted(true);
    const pendingUpload = pendingUploadRef.current;
    const hasDirectoryPaths = pendingUpload?.some(file => file.directoryPath.length > 0) ?? false;
    if (pendingUpload && (!hasDirectoryPaths || directoryStartRequestedRef.current)) {
      pendingUploadRef.current = null;
      const values = form.getValues();
      const mode = hasContractFamilies && values.organiseAsContractFamilies ? values.familyGroupingMode : "none";
      void startUploadRef.current(pendingUpload, mode);
    }
  }, [form, hasContractFamilies]);

  const handleDirectoryStart = React.useCallback(
    (values: ContractFamilyProcessingForm) => {
      if (operationInFlightRef.current) return;
      directoryStartRequestedRef.current = true;
      const pendingUpload = pendingUploadRef.current;
      if (!permissionGrantedRef.current || !pendingUpload) return;
      setUploadStarted(true);
      pendingUploadRef.current = null;
      const familyGroupingMode =
        hasContractFamilies && values.organiseAsContractFamilies ? values.familyGroupingMode : "none";
      void startUploadRef.current(pendingUpload, familyGroupingMode);
    },
    [hasContractFamilies],
  );

  const handlePermissionCancel = React.useCallback(() => {
    pendingUploadRef.current = null;
    onCloseRef.current();
  }, []);

  React.useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    if (initialFiles && initialFiles.length > 0) queueUploadUntilPermissionResolved(initialFiles);
  }, [initialFiles, queueUploadUntilPermissionResolved]);

  const acceptCollectedFiles = React.useCallback(
    (collectedFiles: readonly CollectedUploadFile[]) => {
      const acceptedFiles = collectedFiles.filter(file => validateUploadFile(file.file).length === 0);
      const rejectedRows = collectedFiles.flatMap<FileWithProgress>(collectedFile => {
        const errors = validateUploadFile(collectedFile.file);
        if (errors.length === 0 || collectedFile.file.size === 0) return [];
        return [
          {
            clientId: crypto.randomUUID(),
            file: collectedFile.file,
            relativePath: collectedFile.relativePath,
            progress: 0,
            status: "error",
            error: formatFileErrors(errors),
          },
        ];
      });
      if (rejectedRows.length > 0) updateRows(currentRows => [...currentRows, ...rejectedRows]);
      if (acceptedFiles.length === 0) return;
      setSelectedSourceFiles(acceptedFiles);
      queueUploadUntilPermissionResolved(acceptedFiles);
    },
    [queueUploadUntilPermissionResolved, updateRows],
  );

  const getFilesFromEvent = React.useCallback(async (event: DropzoneFilesEvent) => {
    pendingCollectionRef.current = null;
    const result = await collectDropzoneEventFiles(event);
    if (mountedRef.current) pendingCollectionRef.current = result.outcome ?? null;
    return result.dropzoneFiles;
  }, []);

  const onDrop = React.useCallback(() => {
    const outcome = pendingCollectionRef.current;
    pendingCollectionRef.current = null;
    if (operationInFlightRef.current || !outcome) return;

    if (outcome.error instanceof DirectoryTraversalUnsupportedError) {
      setSelectionMessage(outcome.error.message);
      acceptCollectedFiles(outcome.collectedFiles);
      return;
    }
    if (outcome.error instanceof InvalidDroppedPathError) {
      toast.error(outcome.error.message);
      return;
    }
    if (outcome.error) {
      toast.error("The dropped files could not be read.");
      return;
    }
    acceptCollectedFiles(outcome.collectedFiles);
  }, [acceptCollectedFiles]);

  const handleFolderFiles = React.useCallback(
    (selectedFiles: readonly File[]) => {
      try {
        acceptCollectedFiles(collectSelectedFiles(selectedFiles));
      } catch (error) {
        toast.error(
          error instanceof InvalidDroppedPathError ? error.message : "The selected folder could not be read.",
        );
      }
    },
    [acceptCollectedFiles],
  );

  const changeSelection = React.useCallback(() => {
    if (operationInFlightRef.current || conflictRequestRef.current !== null) return;
    pendingUploadRef.current = null;
    directoryStartRequestedRef.current = false;
    updateRows(() => []);
    setErrorsNotified(false);
    setSelectionMessage(undefined);
    setSelectedSourceFiles([]);
    form.reset({ organiseAsContractFamilies: false, familyGroupingMode: "automatic" });
  }, [form, updateRows]);

  const handleClose = React.useCallback(() => {
    const hasNonTerminalRows = rowsRef.current.some(row => row.status !== "completed" && row.status !== "error");
    if (conflictRequestRef.current !== null) return;
    if (isOperationRunning || hasNonTerminalRows) {
      onCloseRef.current();
      return;
    }
    resolveConflicts(new Set());
    rowsRef.current = [];
    setFiles([]);
    activeTxIdsRef.current = new Set();
    onCloseRef.current();
  }, [isOperationRunning, resolveConflicts]);

  const terminalCount = files.filter(file => file.status === "completed" || file.status === "error").length;
  const completedCount = files.filter(file => file.status === "completed").length;
  const failedCount = files.filter(file => file.status === "error").length;
  const allRowsTerminal = files.length > 0 && terminalCount === files.length;
  const hasNonTerminalRows = files.some(file => file.status !== "completed" && file.status !== "error");
  const operationActive = isOperationRunning || hasNonTerminalRows;
  const aggregateProgress =
    files.length === 0
      ? 0
      : Math.round(files.reduce((sum, file) => sum + normalizeDisplayProgress(file), 0) / files.length);
  const closeBlocked = conflictRequest !== null;
  const sessionBusy = operationActive || closeBlocked;

  React.useLayoutEffect(() => {
    onBusyChangeRef.current?.(sessionBusy);
  }, [sessionBusy]);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operationGenerationRef.current += 1;
      operationInFlightRef.current = false;
      operationInFlightGenerationRef.current = null;
      operationCoordinatorSettledRef.current = true;
      pendingUploadRef.current = null;
      pendingCollectionRef.current = null;
      conflictRequestRef.current?.resolve(new Set());
      conflictRequestRef.current = null;
      onBusyChangeRef.current?.(false);
    };
  }, []);

  React.useEffect(() => {
    if (!allRowsTerminal || closeBlocked) return;
    dispatch(filesApi.util.invalidateTags([FILES_TAGS.FILES_SEARCH_TAG, FILES_TAGS.FILES_TAG]));
    if (selectionMessage) {
      if (errorsNotified) return;
      setErrorsNotified(true);
      toast.warning(selectionMessage);
      return;
    }
    if (failedCount > 0) {
      if (errorsNotified) return;
      setErrorsNotified(true);
      toast.warning(`Uploaded ${files.length - failedCount} of ${files.length} files; ${failedCount} failed`);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      toast.success("Success, your upload has finished");
      handleClose();
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [
    allRowsTerminal,
    closeBlocked,
    selectionMessage,
    dispatch,
    errorsNotified,
    failedCount,
    files.length,
    handleClose,
  ]);

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog,
  } = useDropzone({
    onDrop,
    getFilesFromEvent,
    disabled: sessionBusy,
    minSize: 10,
    accept: ACCEPTED_EXTENSIONS,
    maxSize: MAX_UPLOAD_SIZE,
  });

  if (!isVisible) {
    return <UploadModalWebsocket getActiveTxIds={() => activeTxIdsRef.current} updateRows={updateRows} />;
  }

  const rowErrors = files.filter(file => file.error).map(file => `${file.relativePath} ${file.error}`);
  const errorMessage = [selectionMessage, ...rowErrors].filter(Boolean).join(", ");
  const hasDirectoryPaths = selectedSourceFiles.some(file => file.directoryPath.length > 0);
  const sourceRoots = new Set(selectedSourceFiles.map(file => file.relativePath.split("/")[0]).filter(Boolean));
  const sourceName = sourceRoots.size === 1 ? [...sourceRoots][0] : "Selected files";

  return (
    <FormProvider {...form}>
      <DialogContent
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden p-0"
        closeDisabled={closeBlocked}
        closeDescriptionId={BUSY_DESCRIPTION_ID}
        onEscapeKeyDown={event => closeBlocked && event.preventDefault()}
        onPointerDownOutside={event => closeBlocked && event.preventDefault()}
      >
        {uploadStarted && (
          <Progress
            value={aggregateProgress}
            aria-label="Overall upload progress"
            className="absolute left-0 top-0 h-1 w-full rounded-none bg-slate-200 [&>div]:bg-coral"
          />
        )}
        {conflictRequest && (
          <UploadExistsFilesConfirmationModal
            files={conflictRequest.files}
            onConfirm={resolveConflicts}
            onCancel={() => resolveConflicts(new Set())}
          />
        )}
        <UploadModalPermissionNotification
          fseId={destination.fseId}
          onProceed={handlePermissionProceed}
          onCancel={handlePermissionCancel}
        />
        <DialogHeader className="border-b border-desert-sand-200 px-5 pb-3 pt-4">
          <DialogTitle>{uploadStarted ? `Uploading to ${destination.name}` : "Upload files or folders"}</DialogTitle>
          <p className="truncate pr-6 text-xs text-muted-foreground">
            {uploadStarted
              ? `${sourceName} · ${completedCount} of ${files.length} files complete`
              : `Add documents to ${destination.name}`}
          </p>
        </DialogHeader>
        <UploadModalWebsocket getActiveTxIds={() => activeTxIdsRef.current} updateRows={updateRows} />
        <div className="flex min-h-0 flex-col space-y-4 overflow-y-auto px-5 py-4">
          {!uploadStarted ? (
            <>
              <UploadSelection
                files={selectedSourceFiles}
                disabled={sessionBusy}
                dropzoneProps={getRootProps({
                  "aria-label": "Drop files or folders",
                  "aria-disabled": sessionBusy,
                })}
                inputProps={getInputProps()}
                onChooseFiles={openFileDialog}
                onFolderFiles={handleFolderFiles}
                onChange={changeSelection}
              />
              <form id="contract-family-processing-form" onSubmit={form.handleSubmit(handleDirectoryStart)}>
                <ContractFamilyProcessing
                  disabled={isOperationRunning || uploadStarted}
                  hasDirectoryPaths={hasDirectoryPaths && hasContractFamilies}
                />
              </form>
            </>
          ) : (
            <div className="flex min-h-0 flex-col gap-3">
              {isPreparingFolders && (
                <div
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  className="rounded-md border border-midnight-blue-100 bg-ui-card-background px-4 py-3 text-sm font-medium text-midnight-blue-500"
                >
                  Preparing folders
                </div>
              )}
              <div className="border-b border-desert-sand-200 pb-3">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-midnight-blue-500">
                      {completedCount} of {files.length} files complete
                      {failedCount > 0 ? ` · ${failedCount} failed` : ""}
                    </p>
                    {operationActive && (
                      <p
                        id={BUSY_DESCRIPTION_ID}
                        role="status"
                        aria-live="polite"
                        className="mt-1 text-xs text-muted-foreground"
                      >
                        You can close this window. Processing continues in the background.
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-xl font-semibold tabular-nums text-midnight-blue-500">
                    {aggregateProgress}%
                  </span>
                </div>
              </div>
              <div className="max-h-[360px] overflow-auto">
                <Table className="table-fixed" containerClassName="overflow-x-hidden">
                  <UploadModalFilesHeader />
                  <TableBody>
                    {files.map(fileWithProgress => (
                      <UploadModalFilesRow key={fileWithProgress.clientId} fileWithProgress={fileWithProgress} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          {errorMessage && <UploadModalErrors errorMessage={errorMessage} />}
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-desert-sand-200 px-5 py-3">
          <div>
            {!uploadStarted && hasDirectoryPaths && hasContractFamilies && (
              <p className="text-xs text-muted-foreground">No contract analysis is performed unless enabled.</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant={uploadStarted ? "default" : "outline"}
              onClick={handleClose}
              disabled={closeBlocked}
              aria-describedby={operationActive ? BUSY_DESCRIPTION_ID : undefined}
            >
              {uploadStarted ? "Close" : "Cancel"}
            </Button>
            {hasDirectoryPaths && !uploadStarted && (
              <Button
                type="submit"
                form="contract-family-processing-form"
                aria-label="Start upload"
                disabled={!permissionGranted || isOperationRunning || pendingUploadRef.current === null}
              >
                Upload {selectedSourceFiles.length} {selectedSourceFiles.length === 1 ? "file" : "files"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </FormProvider>
  );
};
